import React, { useState, useEffect } from 'react';
import { manageDailyCode, getCurrentDailyCode } from '../services/adminService';

const AdminDailyCode = () => {
  const [dailyCode, setDailyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [currentCode, setCurrentCode] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchCurrentCode();
  }, []);

  const fetchCurrentCode = async () => {
    setRefreshing(true);
    try {
      const code = await getCurrentDailyCode();
      setCurrentCode(code);
    } catch (error) {
      console.error("Error fetching current code:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dailyCode.trim()) {
      setMessage('Please enter a valid code');
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    setMessage('');
    
    try {
      const result = await manageDailyCode(dailyCode.trim());
      
      if (result.success) {
        setMessageType('success');
        setMessage(result.message);
        await fetchCurrentCode(); // Refresh current code
      } else {
        setMessageType('error');
        setMessage(result.message);
      }
    } catch (error) {
      console.error("Error updating daily code:", error);
      setMessageType('error');
      setMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRandomCode = () => {
    // Generate a random 6-character alphanumeric code
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed similar looking characters
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setDailyCode(result);
  };

  const getMessageStyles = () => {
    switch (messageType) {
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-900';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-200 dark:text-red-900';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-200 dark:text-blue-900';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <svg className="w-6 h-6 mr-2 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
        </svg>
        Daily Code Management
      </h2>
      
      {message && (
        <div className={`p-4 mb-4 rounded-lg ${getMessageStyles()}`}>
          {message}
        </div>
      )}
      
      <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-750">
        <h3 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-2">Current Daily Code</h3>
        {refreshing ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-indigo-500"></div>
            <span className="text-gray-500 dark:text-gray-400">Loading...</span>
          </div>
        ) : currentCode ? (
          <div>
            <div className="flex items-center mb-1">
              <span className="text-lg font-mono font-bold bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 px-3 py-1 rounded-md">
                {currentCode.code}
              </span>
              <button 
                onClick={fetchCurrentCode} 
                className="ml-2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                title="Refresh"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Last updated: {formatDate(currentCode.updatedAt)}
            </p>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No daily code has been set</p>
        )}
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="dailyCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Set New Daily Code
          </label>
          <div className="flex">
            <input
              type="text"
              id="dailyCode"
              value={dailyCode}
              onChange={(e) => setDailyCode(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter new code"
              disabled={loading}
              required
            />
            <button
              type="button"
              onClick={handleGenerateRandomCode}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 border-l-0 rounded-r-md bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loading}
            >
              Generate
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Create a code that users can enter to claim 1,000 coins.
          </p>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>⚠️ Users can claim this reward once per day.</p>
          </div>
          <button
            type="submit"
            className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </>
            ) : (
              'Update Daily Code'
            )}
          </button>
        </div>
      </form>
      
      <div className="mt-6 border-t pt-4 border-gray-200 dark:border-gray-700">
        <h3 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-2">Tips for Daily Codes</h3>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc pl-5">
          <li>Change the code regularly to encourage users to engage daily</li>
          <li>Keep codes simple but not too easy to guess</li>
          <li>Share daily codes through your social media or newsletters</li>
          <li>Consider themed codes for special events or promotions</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminDailyCode; 